import process from 'node:process';
import { pathToFileURL } from 'node:url';
import { installConsoleBranding, shouldBrandConsoleOutput, version } from './brand.js';
import { COMMANDS_WITH_HELP, executeCommand, maybeCheckForUpdate } from './command-registry.js';
import { printVersion, showGlobalHelp } from './help.js';
import {
  ProcessExitSignal,
  createCliUserEventRecorder,
  isExpectedCliInputError,
  recordCliInstrumentationFailure,
} from './user-event-tracking.js';

function normalizeExitCode(value: string | number | null | undefined): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 1;
  }
  return 0;
}

export async function main(argv: string[]) {
  // Rebrand human-readable output for the `cofactor` binary (no-op for
  // `canary`). Skipped for the `mcp` JSON-RPC transport, `--json`/`--format`
  // output, and raw data-query commands, so neither structured payloads nor
  // datastore values are ever rewritten — display branding only.
  if (shouldBrandConsoleOutput(argv)) {
    installConsoleBranding();
  }
  const wantsMachineOutput = argv.includes('--json') || argv.includes('--format');

  if (argv.includes('--version') || argv.includes('-V')) {
    printVersion();
    return;
  }

  const [command, ...rest] = argv;
  const hasHelpFlag = argv.includes('--help') || argv.includes('-h');

  // Global help: --help/‑h without a command that handles it, or explicit "help"
  if (hasHelpFlag && (!command || !COMMANDS_WITH_HELP.has(command))) {
    await showGlobalHelp();
    return;
  }

  if (!command || command === 'help') {
    await showGlobalHelp();
    return;
  }

  if (command === 'version') {
    printVersion();
    return;
  }

  // Non-blocking upgrade check — only for interactive humans logged in via
  // `canary login`. Never nag API-key auth (--token / *_API_TOKEN env var,
  // i.e. CI/automation) or machine-readable (`--json`/`--format`) runs, where
  // the banner is noise that can wrap structured output.
  if (command && !wantsMachineOutput) {
    await maybeCheckForUpdate(command, argv, version);
  }
  let tracking: Awaited<ReturnType<typeof createCliUserEventRecorder>> = null;
  try {
    tracking = await createCliUserEventRecorder(argv, version);
  } catch (error) {
    await recordCliInstrumentationFailure(argv, version, 'recorder-init', error);
  }
  const startedAt = Date.now();
  const originalExit = process.exit.bind(process);
  let finalExitCode = 0;
  let handledExpectedInputError = false;

  if (tracking) {
    await tracking.recorder.recordCommandStarted(tracking.metadata);
  }

  (process as typeof process & { exit: typeof process.exit }).exit = ((code?: number) => {
    throw new ProcessExitSignal(code ?? normalizeExitCode(process.exitCode));
  }) as typeof process.exit;

  try {
    await executeCommand(command, rest);
    finalExitCode = normalizeExitCode(process.exitCode);
    if (tracking) {
      const metadata = {
        ...tracking.metadata,
        durationMs: Date.now() - startedAt,
        exitCode: finalExitCode,
      };
      if (finalExitCode === 0) {
        await tracking.recorder.recordCommandCompleted(metadata);
      } else {
        await tracking.recorder.recordCommandFailed({
          ...metadata,
          errorKind: 'exit-code',
        });
      }
    }
  } catch (error) {
    if (error instanceof ProcessExitSignal) {
      finalExitCode = error.exitCode;
      if (tracking) {
        const metadata = {
          ...tracking.metadata,
          durationMs: Date.now() - startedAt,
          exitCode: finalExitCode,
        };
        if (finalExitCode === 0) {
          await tracking.recorder.recordCommandCompleted(metadata);
        } else {
          await tracking.recorder.recordCommandFailed({
            ...metadata,
            errorKind: 'process-exit',
          });
        }
      }
    } else {
      finalExitCode = normalizeExitCode(process.exitCode) || 1;
      const expectedInputError = isExpectedCliInputError(error);
      if (tracking) {
        await tracking.recorder.recordCommandFailed({
          ...tracking.metadata,
          durationMs: Date.now() - startedAt,
          exitCode: finalExitCode,
          errorKind: error instanceof Error ? error.name : typeof error,
          errorName: error instanceof Error ? error.name : undefined,
          errorMessage: error instanceof Error ? error.message : String(error),
          ...(expectedInputError
            ? {}
            : {
                reportToSentry: true,
                sentryFingerprint: [
                  'canary-cli',
                  tracking.metadata.commandPath,
                  error instanceof Error ? error.name : typeof error,
                ],
              }),
        });
      }
      if (expectedInputError) {
        console.error(`Error: ${error instanceof Error ? error.message : String(error)}`);
        handledExpectedInputError = true;
      } else {
        throw error;
      }
    }
  } finally {
    (process as typeof process & { exit: typeof process.exit }).exit = originalExit;
  }

  if (finalExitCode !== 0 || handledExpectedInputError) {
    originalExit(finalExitCode);
  }
}

if (import.meta.url === pathToFileURL(process.argv[1]).href) {
  void main(process.argv.slice(2));
}
